import { useCallback, useEffect, useMemo, useState } from 'react';
import { claimLead, fetchLeadSection } from '../api';
import { usePolling } from '../hooks/usePolling';
import type { Lead } from '../types';
import LeadCard from './LeadCard';
import { AgentStatusWidget } from './AgentStatusWidget';

type LeadView = 'unassigned' | 'queue';

const SOURCE_FILTERS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'meta_form', label: 'Meta form' },
  { value: 'call', label: 'Call' },
  { value: 'neodove', label: 'NeoDove' },
] as const;

export default function QueueTab({
  active,
  onSelect,
  onChanged,
  reloadKey,
  me,
  selectedId,
  view,
  title,
  subtitle,
}: {
  active: boolean;
  onSelect: (id: string) => void;
  onChanged: () => void;
  reloadKey: number;
  me: string;
  selectedId?: string | null;
  view: LeadView;
  title: string;
  subtitle: string;
}) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);

  const sortedLeads = useMemo(() => {
    return [...leads].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [leads]);

  const load = useCallback(async () => {
    try {
      setRefreshing(true);
      const d = await fetchLeadSection(view, {
        limit: 50,
        source: view === 'unassigned' ? selectedSources : undefined,
      });
      setLeads(d.leads);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'load failed');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [selectedSources, view]);

  useEffect(() => {
    if (!active) return;
    void load();
  }, [active, load, reloadKey]);

  usePolling(load, 60_000, [view, reloadKey, selectedSources.join(',')], {
    enabled: active,
    immediate: false,
  });

  const toggleSource = (source: string) => {
    setSelectedSources((current) =>
      current.includes(source) ? current.filter((item) => item !== source) : [...current, source]
    );
  };

  async function handleClaim(id: string) {
    try {
      await claimLead(id);
      onChanged();
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Claim failed');
    }
  }

  return (
    <div className="space-y-3 px-3 py-3">
      {view === 'queue' && <AgentStatusWidget agentId={me} active={active} />}

      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          <div className="text-xs text-slate-500">{subtitle}</div>
        </div>
        {refreshing && <span className="text-[10px] uppercase tracking-wider text-slate-400">…updating</span>}
      </div>

      {view === 'unassigned' && (
        <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Source filter</div>
              <div className="text-xs text-slate-400">
                {selectedSources.length === 0
                  ? 'All sources'
                  : `Filtered: ${selectedSources.length} selected`}
              </div>
            </div>
            {selectedSources.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedSources([])}
                className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {SOURCE_FILTERS.map((source) => {
              const checked = selectedSources.includes(source.value);
              return (
                <button
                  key={source.value}
                  type="button"
                  onClick={() => toggleSource(source.value)}
                  className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                    checked
                      ? 'border-wechat-accent bg-wechat-accent text-white shadow-sm'
                      : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                  }`}
                  aria-pressed={checked}
                >
                  <span
                    className={`h-2 w-2 rounded-full ${
                      checked ? 'bg-white' : 'bg-slate-300'
                    }`}
                  />
                  {source.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {loading && <p className="py-10 text-center text-sm text-slate-500">Loading…</p>}
      {err && <p className="rounded-lg bg-red-50 p-3 text-center text-sm text-red-700">{err}</p>}
      {!loading && !err && leads.length === 0 && (
        <p className="py-16 text-center text-sm text-slate-500">
          {view === 'unassigned'
            ? 'No unassigned leads right now.'
            : 'Nothing in your queue right now.'}
        </p>
      )}

      <div className="space-y-3">
        {sortedLeads.map((l) => (
          <LeadCard
            key={l.id}
            lead={l}
            onSelect={onSelect}
            me={me}
            showStatus
            selected={selectedId === l.id}
            action={
              view === 'unassigned'
                ? {
                    label: 'Assign to Me',
                    onClick: handleClaim,
                  }
                : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}
